import React, { useState, useEffect } from 'react';
import Header from '../Components/Header';
import Footer from '../Components/Footer';
import './Cookies.css';

function Cookies() {
  const [activeSection, setActiveSection] = useState(null);
  const [animateIcons, setAnimateIcons] = useState(false);

  useEffect(() => {
    // Trigger initial animations
    setTimeout(() => {
      setAnimateIcons(true);
    }, 500);

    // Animation for sections appearing on scroll
    const sections = document.querySelectorAll('.cookies-section');
    
    const observerOptions = {
      threshold: 0.15,
      rootMargin: "0px 0px -50px 0px"
    };
    
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('appear');
          observer.unobserve(entry.target);
        }
      });
    }, observerOptions);
    
    sections.forEach(section => {
      observer.observe(section);
    });
    
    return () => {
      sections.forEach(section => {
        observer.unobserve(section);
      });
    };
  }, []);

  const toggleSection = (index) => {
    setActiveSection(activeSection === index ? null : index);
  };

  return (
    <div className="cookies-container">
      <Header />
      
      <div className="cookies-hero">
        <div className="cookies-hero-content">
          <div className={`cookies-icon-container ${animateIcons ? 'animate' : ''}`}>
            <div className="cookies-icon">🍪</div>
            <div className="cookies-icon-ring ring1"></div>
            <div className="cookies-icon-ring ring2"></div>
            <div className="cookies-icon-ring ring3"></div>
          </div>
          <h1 className="cookies-title">Utilisation des Cookies</h1>
          <p className="cookies-subtitle">Des cookies simples pour une expérience optimale</p>
          <p className="cookies-date">Dernière mise à jour : 11 mars 2025</p>
        </div>
        <div className="cookies-wave">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0 0L60 10C120 20 240 40 360 46.7C480 53.3 600 46.7 720 53.3C840 60 960 80 1080 80C1200 80 1320 60 1380 50L1440 40V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0V0Z" fill="white"/>
          </svg>
        </div>
      </div>
      
      <div className="cookies-content">
        <div className="cookies-intro cookies-section">
          <p>
            Chez InstalLoc, nous valorisons votre vie privée et votre expérience utilisateur. 
            Notre approche des cookies est minimaliste et transparente : <strong>nous utilisons 
            uniquement les cookies essentiels</strong> au bon fonctionnement de notre site.
          </p>
          
          <div className="cookies-highlights">
            <div className="cookies-highlight">
              <div className="cookies-highlight-icon">✨</div>
              <h3>Simplicité</h3>
              <p>Uniquement des cookies essentiels</p>
            </div>
            <div className="cookies-highlight">
              <div className="cookies-highlight-icon">🛡️</div>
              <h3>Protection</h3>
              <p>Aucune donnée de suivi</p>
            </div>
            <div className="cookies-highlight">
              <div className="cookies-highlight-icon">💯</div>
              <h3>Transparence</h3>
              <p>Contrôle total pour vous</p>
            </div>
          </div>
        </div>
        
        <div className="cookies-explainer cookies-section">
          <div className="cookies-card">
            <h2>Qu'est-ce qu'un cookie ?</h2>
            <p>
              Un cookie est un petit fichier texte stocké par votre navigateur lorsque vous 
              consultez un site web. Ces fichiers permettent au site de mémoriser vos actions et 
              préférences pendant une période déterminée, vous évitant ainsi de les réindiquer 
              à chaque fois que vous visitez le site.
            </p>
            <div className="cookies-illustration">
              <div className="cookies-browser">
                <div className="cookies-browser-header">
                  <div className="cookies-browser-buttons">
                    <span></span><span></span><span></span>
                  </div>
                  <div className="cookies-browser-url">instaloc.fr</div>
                </div>
                <div className="cookies-browser-content">
                  <div className="cookies-browser-cookie">
                    <div className="cookies-browser-cookie-text">Cookie essentiel</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div className="cookies-types cookies-section">
          <h2>Nos cookies essentiels</h2>
          <div className="cookies-accordion">
            <div className={`cookies-accordion-item ${activeSection === 0 ? 'active' : ''}`} onClick={() => toggleSection(0)}>
              <div className="cookies-accordion-header">
                <h3>Cookies de session</h3>
                <span className="cookies-accordion-icon"></span>
              </div>
              <div className="cookies-accordion-content">
                <p>
                  Ces cookies temporaires sont nécessaires pour assurer votre navigation entre les pages 
                  et maintenir votre session authentifiée. Ils sont automatiquement supprimés lorsque 
                  vous fermez votre navigateur.
                </p>
                <div className="cookies-spec">
                  <div className="cookies-spec-item">
                    <span className="cookies-spec-label">Durée de vie</span>
                    <span className="cookies-spec-value">Session</span>
                  </div>
                  <div className="cookies-spec-item">
                    <span className="cookies-spec-label">Finalité</span>
                    <span className="cookies-spec-value">Technique</span>
                  </div>
                </div>
              </div>
            </div>
            
            <div className={`cookies-accordion-item ${activeSection === 1 ? 'active' : ''}`} onClick={() => toggleSection(1)}>
              <div className="cookies-accordion-header">
                <h3>Cookies de sécurité</h3>
                <span className="cookies-accordion-icon"></span>
              </div>
              <div className="cookies-accordion-content">
                <p>
                  Ces cookies permettent d'assurer la sécurité de votre navigation en prévenant 
                  les tentatives de fraude et en protégeant vos données. Ils sont indispensables 
                  à la sécurisation de nos services.
                </p>
                <div className="cookies-spec">
                  <div className="cookies-spec-item">
                    <span className="cookies-spec-label">Durée de vie</span>
                    <span className="cookies-spec-value">1 an maximum</span>
                  </div>
                  <div className="cookies-spec-item">
                    <span className="cookies-spec-label">Finalité</span>
                    <span className="cookies-spec-value">Sécurité</span>
                  </div>
                </div>
              </div>
            </div>
            
            <div className={`cookies-accordion-item ${activeSection === 2 ? 'active' : ''}`} onClick={() => toggleSection(2)}>
              <div className="cookies-accordion-header">
                <h3>Cookies de préférences</h3>
                <span className="cookies-accordion-icon"></span>
              </div>
              <div className="cookies-accordion-content">
                <p>
                  Ces cookies conservent vos préférences d'affichage, comme le mode clair/sombre 
                  ou vos paramètres d'accessibilité, pour vous offrir une expérience personnalisée 
                  à chaque visite.
                </p>
                <div className="cookies-spec">
                  <div className="cookies-spec-item">
                    <span className="cookies-spec-label">Durée de vie</span>
                    <span className="cookies-spec-value">6 mois</span>
                  </div>
                  <div className="cookies-spec-item">
                    <span className="cookies-spec-label">Finalité</span>
                    <span className="cookies-spec-value">Confort</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div className="cookies-not-used cookies-section">
          <h2>Ce que Instaloc.fr n'utilise pas</h2>
          <div className="cookies-not-used-items">
            <div className="cookies-not-used-item">
              <div className="cookies-not-used-icon">
                <span>🚫</span>
              </div>
              <h3>Cookies publicitaires</h3>
              <p>Nous ne diffusons aucune publicité ciblée et n'utilisons pas de cookies pour suivre votre navigation à des fins publicitaires.</p>
            </div>
            
            <div className="cookies-not-used-item">
              <div className="cookies-not-used-icon">
                <span>🚫</span>
              </div>
              <h3>Cookies d'analyse</h3>
              <p>Nous n'utilisons pas de cookies pour analyser votre comportement de navigation ou créer des profils d'utilisation.</p>
            </div>
            
            <div className="cookies-not-used-item">
              <div className="cookies-not-used-icon">
                <span>🚫</span>
              </div>
              <h3>Cookies tiers</h3>
              <p>Nous n'autorisons aucun tiers à déposer des cookies sur votre appareil via notre site web.</p>
            </div>
          </div>
        </div>
        
        <div className="cookies-control cookies-section">
          <div className="cookies-control-card">
            <h2>Comment gérer vos cookies</h2>
            <p>
              Bien que nous n'utilisions que des cookies essentiels, vous pouvez toujours contrôler 
              ces paramètres via votre navigateur. Voici comment gérer ou supprimer les cookies :
            </p>
            
            <div className="cookies-browsers">
              <div className="cookies-browser-option">
                <div className="cookies-browser-logo chrome"></div>
                <h3>Chrome</h3>
                <p>Paramètres → Confidentialité et sécurité → Cookies</p>
              </div>
              
              <div className="cookies-browser-option">
                <div className="cookies-browser-logo firefox"></div>
                <h3>Firefox</h3>
                <p>Paramètres → Vie privée → Historique → Cookies</p>
              </div>
              
              <div className="cookies-browser-option">
                <div className="cookies-browser-logo safari"></div>
                <h3>Safari</h3>
                <p>Préférences → Confidentialité → Cookies</p>
              </div>
              
              <div className="cookies-browser-option">
                <div className="cookies-browser-logo edge"></div>
                <h3>Edge</h3>
                <p>Paramètres → Confidentialité → Effacer les données de navigation</p>
              </div>
            </div>
            
            <div className="cookies-note">
              <p>
                <strong>Note importante :</strong> La désactivation des cookies essentiels peut 
                affecter le bon fonctionnement du site et limiter votre expérience utilisateur.
              </p>
            </div>
          </div>
        </div>
        
        <div className="cookies-updates cookies-section">
          <h2>Mises à jour</h2>
          <p>
            Nous nous réservons le droit de modifier cette politique relative aux cookies à tout moment. 
            Toute modification sera publiée sur cette page avec une date de mise à jour actualisée.
          </p>
          <p>
            Si vous avez des questions concernant notre utilisation des cookies, n'hésitez pas à nous contacter à{" "}
            <a href="mailto:contact@instaloc.fr" className="cookies-link">
              contact@instaloc.fr
            </a>
          </p>
        </div>
      </div>
      
      <Footer />
    </div>
  );
}

export default Cookies;